import { useCallback, useState } from 'react'

import { useCreateFiles } from '@tribeplatform/react-sdk/hooks'

import { Attachment, HandleAttachmentSelect } from '../@types'
import { composerError } from '../utils/errors'

export type handleAttachmentSelect = (files: File[]) => Promise<void>

export const useAttachments = (initialAttachments = []) => {
  const [attachments, setAttachments] =
    useState<Attachment[]>(initialAttachments)

  const { mutateAsync: uploadFiles } = useCreateFiles()

  const handleAttachmentSelect: HandleAttachmentSelect = useCallback(
    async (files: File[]) => {
      if (!files?.length) return

      const tempFileData = files.map(
        ({ name, size }) =>
          ({
            id: `${name}-${size}`,
            url: '',
            downloadUrl: '',
            size,
            extension: name.slice(name.lastIndexOf('.') + 1).toLowerCase(),
            name,
            isLoading: true,
          } as Attachment),
      )

      setAttachments(attachments => [...tempFileData, ...attachments])

      let uploadedFiles = null

      try {
        uploadedFiles = await uploadFiles(
          files.map(file => {
            const { type, name, size } = file

            return {
              file,
              contentType: type,
              name,
              extension: name.split('.').pop().toLowerCase() || '',
              size,
            }
          }),
        )

        setAttachments(attachments => [
          ...uploadedFiles?.map(uploadedFile => ({
            ...uploadedFile,
            isLoading: false,
          })),
          ...attachments.filter(a => !a.isLoading),
        ])
      } catch (error) {
        // Revert back attachments
        setAttachments(attachments => [
          ...attachments.filter(a => !a.isLoading),
        ])

        // Show the error notification
        composerError(error.message)
      }
    },
    [uploadFiles],
  )

  const resetAttachments = useCallback(() => setAttachments([]), [])

  const handleAttachmentDelete = useCallback(
    (attachment: Attachment) => {
      // to reset send the attachment as null
      if (attachment === null) {
        resetAttachments()
      } else {
        setAttachments(attachments =>
          attachments.filter(({ id }) => id !== attachment.id),
        )
      }
    },
    [resetAttachments],
  )

  return {
    attachments,
    resetAttachments,
    handleAttachmentSelect,
    handleAttachmentDelete,
  }
}
